import React, { useEffect, useMemo, useRef, useState } from "react"
import Button from "../ui/Button"
import { RotateCcw } from "lucide-react"
import PriceAlert from "./PriceAlert"
import PriceFilter from "./PriceFilter"
import Transfers from "./Transfers"
import Duration from "./Duration"
import Countries from "./Countries"
import Airports from "./Airports"
import Airlines from "./Airlines"
import SkeletonLoader from "./SkeletonLoader"
import { useDispatch, useSelector } from "react-redux"
import {
  resetAllExceptProtectedTerminals,
  resetAviaCountriesExceptProtected,
  selectAviaCountryById,
  selectAviaSearchCurrency,
  selectAviaSearchTerminals,
  selectAviaSidebarFilters,
  selectAviaTerminalById,
  selectSelectedArrivalCountries,
  toggleSelectAllAviaCountries,
} from "../../../../../app/reducers/book/aviaSearchSlice"
import { CURRENCIES } from "../../../../../variables/common"
import { useTranslation } from "react-i18next"
import { collectSelectedTerminals } from "../../utils/processing/search"
import { syncCountrySelection } from "../../utils/filters/serch"
import { useInitialSelectedId } from "../../hooks/useInitialSelectedId"
import { useInitialSelectedIds } from "../../hooks/useInitialSelectedIds"
import FiltersContainer from "./FiltersContainer"
import { fetchAirlines } from "../../../../../shared/api/airFetcher"

const FlightSidebar = ({
  isLoading = true,
  filters,
  onChange,
  handleResetFilters,
  airlines,
  shouldShowSidebar,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const currency = useSelector(selectAviaSearchCurrency)
  const foundCurrency = CURRENCIES.find((item) => item.code === currency)
  const { arrivalTerminals } = useSelector(selectAviaSearchTerminals)

  const [priceAlert, setPriceAlert] = useState(false)
  const [priceRange, setPriceRange] = useState([0, 10000])
  const [airports, setAirports] = useState([])

  const computedInitialCountries = useMemo(
    () =>
      arrivalTerminals
        .filter((country) => country.terminals?.some((terminal) => terminal.is_selected))
        .map((country) => ({ ...country, is_selected: true })),
    [shouldShowSidebar]
  )

  const initialCountries = useRef(computedInitialCountries)

  const [countries, setCountries] = useState(computedInitialCountries)

  useEffect(() => {
    initialCountries.current = computedInitialCountries
    setCountries(computedInitialCountries)
  }, [shouldShowSidebar])

  const [transfers, setTransfers] = useState({
    direct: {
      label: "Прямой рейс",
      checked: true,
    },
    max1: {
      label: "Не более 1 пересадки",
      checked: true,
    },
    max2: {
      label: "Не более 2 пересадок",
      checked: true,
    },
    max3: {
      label: "Не более 3 пересадок",
      checked: false,
    },
  })

  const initialSelectedCountryIds = useInitialSelectedIds({
    items: countries,
    selectedKey: "is_selected",
    idKey: "country_code",
  })

  const initialSelectedAirportIds = useInitialSelectedIds({
    idKey: "id",
    items: airports,
    selectedKey: "is_selected",
  })

  useEffect(() => {
    if (countries.length > 0) {
      const selectedCodes = countries.filter((c) => c.is_selected).map((c) => c.country_code)

      const merged = arrivalTerminals
        .filter((c) => selectedCodes.includes(c.country_code))
        .map((c) => ({ ...c, is_selected: true }))

      setAirports(collectSelectedTerminals(merged))
    }
  }, [countries, arrivalTerminals])

  if (isLoading) {
    return <SkeletonLoader />
  }

  const handleCountryChange = (code) => {
    let updatedCountries

    if (code === "all") {
      updatedCountries = countries.map((c) => ({ ...c, is_selected: true }))
    } else if (code === "reset") {
      updatedCountries = countries.map((c, i) => ({ ...c, is_selected: i === 0 }))
    } else {
      updatedCountries = countries.map((c) =>
        c.country_code === code ? { ...c, is_selected: !c.is_selected } : c
      )
    }

    setCountries(updatedCountries)

    // const selectedCountryCodes = updatedCountries
    //   .filter((c) => c.is_selected)
    //   .map((c) => c.country_code)

    // const selectedTerminalIds = arrivalTerminals
    //   .filter((c) => selectedCountryCodes.includes(c.country_code))
    //   .flatMap((c) => c.terminals?.map((t) => t.id) ?? [])

    // dispatch(
    //   resetAllExceptProtectedTerminals({
    //     protectedTerminals: selectedTerminalIds,
    //   })
    // )
  }

  // const handleAirportChange = (id) => {
  //   if (id === "reset") {
  //     dispatch(
  //       resetAllExceptProtectedTerminals({
  //         protectedTerminals: initialSelectedAirportIds.current.length
  //           ? [initialSelectedAirportIds.current[0]]
  //           : [],
  //       })
  //     )
  //   } else {
  //     dispatch(selectAviaTerminalById({ terminalId: id, type: "arrival" }))
  //   }
  // }

  // const handleAirportChange = (id) => {
  //   if (id === "reset") {
  //     onChange({ key: "terminalIds", value: [], overwrite: true })
  //     setAirports((prev) =>
  //       prev.map((airport, index) => ({
  //         ...airport,
  //         is_selected: initialSelectedAirportIds.current.includes(airport.id)
  //           ? true
  //           : index === 0
  //             ? true
  //             : false,
  //       }))
  //     )
  //   } else {
  //     onChange({ key: "terminalIds", value: id })
  //     setAirports((prev) =>
  //       prev.map((airport) =>
  //         airport.id === id ? { ...airport, is_selected: !airport.is_selected } : airport
  //       )
  //     )
  //   }
  // }

  const handleAirportChange = (id) => {
    if (id === "reset") {
      onChange({ key: "terminalIds", value: [], overwrite: true })
      setAirports((prev) =>
        prev.map((airport) => ({
          ...airport,
          is_selected: initialSelectedAirportIds.current.includes(airport.id),
        }))
      )
    } else {
      const currentSelected = airports.filter((a) => a.is_selected).map((a) => a.id)

      const isCurrentlySelected = currentSelected.includes(id)

      const newSelected = isCurrentlySelected
        ? currentSelected.filter((i) => i !== id)
        : [...currentSelected, id]

      onChange({ key: "terminalIds", value: newSelected, overwrite: true })

      setAirports((prev) =>
        prev.map((airport) =>
          airport.id === id ? { ...airport, is_selected: !airport.is_selected } : airport
        )
      )
    }
  }

  return (
    <FiltersContainer
      filters={filters}
      priceRange={priceRange}
      foundCurrency={foundCurrency}
      countries={countries}
      airports={airports}
      airlines={airlines}
      initialSelectedCountryIds={
        initialSelectedCountryIds.current.length ? [initialSelectedCountryIds.current[0]] : []
      }
      initialSelectedAirportIds={
        initialSelectedAirportIds.current.length ? [initialSelectedAirportIds.current[0]] : []
      }
      onChange={onChange}
      handleCountryChange={handleCountryChange}
      handleAirportChange={handleAirportChange}
      handleResetFilters={handleResetFilters}
    />
  )
}

export default FlightSidebar
